import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { I18nextProvider } from 'react-i18next';
import { loadUserSession } from "./store/authSlice";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { DataProvider } from "./context/DataContext";
import AppRoutes from "./Routes";
import i18n from './i18n/config';
import { supabase } from "./lib/supabaseClient";
import "./styles/index.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function App() {
  const dispatch = useDispatch();
  const [isAppReady, setIsAppReady] = useState(false);

  useEffect(() => {
    const initializeApp = async () => {
      // Initial session restoration
      await dispatch(loadUserSession());
      // Permanently drop the barrier for this session
      setIsAppReady(true);
    };

    initializeApp();

    const verifySession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;

        const { data: { user }, error } = await supabase.auth.getUser();
        if (error || !user) {
          console.warn("Session validation failed on server. Logging out...", error);
          sessionStorage.clear();
          localStorage.clear();
          dispatch({ type: 'auth/logoutUser/fulfilled' });
        }
      } catch (err) {
        console.error("Session verification error:", err);
      }
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT' || (event === 'TOKEN_REFRESHED' && !session)) {
        sessionStorage.clear();
        localStorage.clear();
        dispatch({ type: 'auth/logoutUser/fulfilled' });
      }
    });

    // Check periodically every 10 seconds
    const interval = setInterval(verifySession, 10000);

    // Also check immediately when page gains focus
    window.addEventListener('focus', verifySession);

    return () => {
      subscription?.unsubscribe();
      clearInterval(interval);
      window.removeEventListener('focus', verifySession);
    };
  }, [dispatch]);

  // ABSOLUTE INITIALIZATION BARRIER
  // Only triggers on the very first load of the app.
  if (!isAppReady) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-card">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          <p className="text-lg font-medium text-muted-foreground animate-pulse">Starting Application...</p>
        </div>
      </div>
    );
  }

  return (
    <I18nextProvider i18n={i18n}>
      <QueryClientProvider client={queryClient}>
        <DataProvider>
          <AppRoutes />
        </DataProvider>
      </QueryClientProvider>
    </I18nextProvider>
  );
}

export default App;